/**
 * ArcadeSounds - 8-Bit Sound Effects Engine
 * Authentic arcade cabinet sounds using Web Audio API
 */

export class ArcadeSounds {
  private audioContext: AudioContext;
  private masterVolume: number;

  constructor() {
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    this.masterVolume = 0.3; // Keep it reasonable
  }

  /**
   * Button Click Sound (Coin Insert)
   * Classic arcade coin-drop beep
   */
  playClick() {
    const osc1 = this.audioContext.createOscillator();
    const osc2 = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    osc1.frequency.value = 800;
    osc2.frequency.value = 1600;
    osc1.type = 'square';
    osc2.type = 'square';

    gainNode.gain.setValueAtTime(this.masterVolume, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);

    osc1.connect(gainNode);
    osc2.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    osc1.start(this.audioContext.currentTime);
    osc2.start(this.audioContext.currentTime);
    osc1.stop(this.audioContext.currentTime + 0.1);
    osc2.stop(this.audioContext.currentTime + 0.1);
  }

  /**
   * Hover Sound (Menu Beep)
   * Subtle navigation feedback
   */
  playHover() {
    const osc = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    osc.frequency.value = 400;
    osc.type = 'square';

    gainNode.gain.setValueAtTime(this.masterVolume * 0.3, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.05);

    osc.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    osc.start(this.audioContext.currentTime);
    osc.stop(this.audioContext.currentTime + 0.05);
  }

  /**
   * Success Sound (Power-Up)
   * C-E-G-C chord progression for success feedback
   */
  playSuccess() {
    const notes = [261.63, 329.63, 392.00, 523.25]; // C-E-G-C chord

    notes.forEach((freq, i) => {
      setTimeout(() => {
        const osc = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        osc.frequency.value = freq;
        osc.type = 'square';

        gainNode.gain.setValueAtTime(this.masterVolume, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2);

        osc.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        osc.start(this.audioContext.currentTime);
        osc.stop(this.audioContext.currentTime + 0.2);
      }, i * 50);
    });
  }

  /**
   * Error Sound (Game Over)
   * Descending tone for errors/rejections
   */
  playError() {
    const osc = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(200, this.audioContext.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, this.audioContext.currentTime + 0.3);

    gainNode.gain.setValueAtTime(this.masterVolume, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

    osc.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    osc.start(this.audioContext.currentTime);
    osc.stop(this.audioContext.currentTime + 0.3);
  }

  /**
   * Level Up Sound (Konami Code Reward)
   * Ascending C-E-G-C octave for major achievements
   */
  playLevelUp() {
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C-E-G-C octave up

    notes.forEach((freq, i) => {
      setTimeout(() => {
        const osc = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        osc.frequency.value = freq;
        osc.type = 'triangle';

        gainNode.gain.setValueAtTime(this.masterVolume * 0.5, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

        osc.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        osc.start(this.audioContext.currentTime);
        osc.stop(this.audioContext.currentTime + 0.3);
      }, i * 100);
    });
  }
}

// Global instance (initialized on first user interaction)
let arcadeSoundsInstance: ArcadeSounds | null = null;

export const initArcadeSounds = () => {
  if (!arcadeSoundsInstance) {
    arcadeSoundsInstance = new ArcadeSounds();
  }
  return arcadeSoundsInstance;
};

export const getArcadeSounds = () => arcadeSoundsInstance;
